import { useState, useEffect, useRef } from 'react';

import Request from '~/providers/Request';

const PATH = '/admin/medical_record_evolution_drafts';
const SAVE_DELAY = 1500;
const REQUEST_CONFIG = { normalizeJSON: false, disableToastErrors: true };

const plainText = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent.replace(/\s+/g, ' ').trim();
};

const sameStatus = (a, b) =>
  (a && a.state) === (b && b.state) && (a && a.at) === (b && b.at);

function useEvolutionDraft({ isOpen, idClient, notes, emptyNotes, onLoad }) {
  const [status, setStatus] = useState(null);

  const notesRef = useRef(notes);
  const savedRef = useRef(null);
  const savedAtRef = useRef(null);
  const activeRef = useRef(false);
  const timerRef = useRef(null);
  const queueRef = useRef(Promise.resolve());

  notesRef.current = notes;

  const isEmpty = (text) => !text || text === plainText(emptyNotes);

  const isPending = (text) =>
    text !== savedRef.current && !(isEmpty(text) && savedRef.current === null);

  function syncStatus() {
    let next = null;
    if (isPending(plainText(notesRef.current))) {
      next = { state: 'saving' };
    } else if (savedRef.current !== null) {
      next = { state: 'saved', at: savedAtRef.current };
    }

    setStatus((current) => (sameStatus(current, next) ? current : next));
  }

  async function persist(value) {
    const text = plainText(value);

    try {
      if (isPending(text)) {
        if (isEmpty(text)) {
          await Request.del(`${PATH}/${idClient}`, null, REQUEST_CONFIG);
          savedRef.current = null;
        } else {
          const draft = await Request.put(
            PATH,
            { user_id: idClient, notes: value },
            null,
            REQUEST_CONFIG
          );
          savedRef.current = text;
          savedAtRef.current = draft.updated_at;
        }
      }
      syncStatus();
    } catch (error) {
      setStatus({ state: 'error' });
    }
  }

  function flush() {
    clearTimeout(timerRef.current);
    if (!activeRef.current) return queueRef.current;

    const value = notesRef.current;
    queueRef.current = queueRef.current.then(() => persist(value));
    return queueRef.current;
  }

  function settle() {
    clearTimeout(timerRef.current);
    activeRef.current = false;
    return queueRef.current;
  }

  function resume() {
    activeRef.current = true;
    flush();
  }

  useEffect(() => {
    if (!isOpen || !idClient) return undefined;

    let cancelled = false;
    savedRef.current = null;
    savedAtRef.current = null;
    setStatus(null);

    queueRef.current = queueRef.current
      .then(() => Request.get(`${PATH}/${idClient}`, null, REQUEST_CONFIG))
      .then((draft) => {
        if (cancelled || !draft.notes) return;
        savedRef.current = plainText(draft.notes);
        savedAtRef.current = draft.updated_at;
        setStatus({ state: 'saved', at: draft.updated_at });
        if (plainText(notesRef.current) === plainText(emptyNotes)) {
          onLoad(draft.notes);
        }
      })
      .catch(() => {})
      .then(() => {
        if (!cancelled) activeRef.current = true;
      });

    return () => {
      cancelled = true;
      flush();
      activeRef.current = false;
    };
  }, [isOpen, idClient]);

  useEffect(() => {
    if (!activeRef.current) return undefined;

    syncStatus();
    timerRef.current = setTimeout(flush, SAVE_DELAY);
    return () => clearTimeout(timerRef.current);
  }, [notes]);

  return { status, flush, settle, resume };
}

export default useEvolutionDraft;
